import * as React from "react";

import { cn } from "@/lib/utils";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

function useAutofillSafeAttrs(
  autoComplete: string | undefined,
  name: string | undefined,
  id: string | undefined,
) {
  const suffix = React.useMemo(
    () => Math.random().toString(36).slice(2, 8),
    [],
  );
  const isAutofillOptIn = autoComplete && autoComplete !== "off";
  if (isAutofillOptIn) return { safeName: name, safeId: id };
  return {
    safeName: name ? `${name}_${suffix}` : `field_${suffix}`,
    safeId: id ? `${id}_${suffix}` : undefined,
  };
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoComplete, name, id, onFocus, readOnly, ...props }, ref) => {
    // SECURITY: same multi-layer autofill defence as <Input>. See the
    // comment block in src/components/ui/input.tsx for the rationale.
    const effectiveAutoComplete = autoComplete ?? "off";
    const isAutofillOptIn = autoComplete && autoComplete !== "off";
    const { safeName, safeId } = useAutofillSafeAttrs(autoComplete, name, id);

    const [autofillBlocked, setAutofillBlocked] = React.useState(
      !isAutofillOptIn && readOnly !== true,
    );

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (autofillBlocked) setAutofillBlocked(false);
      onFocus?.(e);
    };

    return (
      <textarea
        name={safeName}
        id={safeId}
        autoComplete={effectiveAutoComplete}
        data-form-type={isAutofillOptIn ? undefined : "other"}
        data-lpignore={isAutofillOptIn ? undefined : "true"}
        data-1p-ignore={isAutofillOptIn ? undefined : "true"}
        readOnly={readOnly || autofillBlocked}
        onFocus={handleFocus}
        className={cn(
          "flex min-h-[80px] w-full rounded-lg border border-[rgba(13,31,20,0.16)] bg-white px-3.5 py-2.5 text-sm font-body text-nursly-ink ring-offset-background placeholder:text-nursly-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nursly-sage focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
